// D27 "+" new-chat picker — role scope + recipient bucketing (frame 5m).
//
// The picker is ROLE-SCOPED: who appears, and whether they are reached DIRECTLY
// (a "Chat" button) or VIA A MESSAGE REQUEST (a "Request" button), depends on
// the viewer's role. It NEVER bypasses the message-request gate — a
// reachable_via:"request" recipient always routes through the compose flow
// (D12 cap applies); reachable_via just says WHICH flow, not that it's free.
//
// Two data sources feed the picker (be-core):
//   • /dashboard/chat/recipients/       → SAME-ORG colleagues (always DIRECT).
//   • /dashboard/chat/recipient-search/ → cross-party rows, each tagged with
//     kind (agent|club) and reachable_via (request|direct) + a role_scope.
//
// These pure helpers normalise both shapes into one row model and split them
// into the DIRECT / VIA-REQUEST sections, so the UI and its tests share one rule.

import { ROLES } from "../../config/roles";

const has = (roles, r) => Array.isArray(roles) && roles.includes(r);

// The viewer's picker capability from their role set. Used to gate the empty
// state / helper copy; the recipient-search role_scope is authoritative for what
// the endpoint actually returns.
export const pickerScopeForRole = (roles) => {
  const isAgent = has(roles, ROLES.Agent) || has(roles, ROLES.AgencyAdmin);
  const isClub = has(roles, ROLES.ClubAdmin) || has(roles, ROLES.Staff);
  return { isAgent, isClub, canSendRequests: isAgent };
};

// A /recipients colleague row → the common model. Colleagues are always DIRECT
// (same-org, timerless); they carry a deterministic channel_id_candidate.
export const normalizeContactRow = (r) => ({
  key: `c-${r.account_id}`,
  name: r.name,
  subtitle: [r.agency_name || r.club_name, "your colleague · timerless"]
    .filter(Boolean)
    .join(" · "),
  avatar: r.profile_picture_url || null,
  account_id: r.account_id ?? null,
  agent_id: null,
  club_id: r.club_id ?? null,
  channel_id_candidate: r.channel_id_candidate || null,
  bucket: "direct",
  kind: r.agency_id != null || r.agency_name ? "agent" : "club",
});

// A /recipient-search row → the common model. reachable_via decides the bucket;
// the subtitle gets the cross-agency tail for a request-agent, the country/agency
// for the rest.
export const normalizeSearchRow = (r) => {
  const isRequest = r.reachable_via === "request";
  let subtitle = r.subtitle || "";
  if (r.kind === "agent" && isRequest) {
    subtitle = [r.subtitle, "cross-agency"].filter(Boolean).join(" · ");
  }
  return {
    key: `s-${r.kind}-${r.agent_id ?? r.club_id ?? r.account_id}`,
    name: r.name,
    subtitle,
    avatar: r.avatar_url || null,
    account_id: r.account_id ?? null,
    agent_id: r.agent_id ?? null,
    club_id: r.club_id ?? null,
    channel_id_candidate: null,
    bucket: isRequest ? "request" : "direct",
    kind: r.kind,
  };
};

/**
 * Merge the two sources into the picker's sections.
 *
 * @param {object} args
 * @param {Array}  args.contacts   /recipients rows (same-org colleagues)
 * @param {Array}  args.search     /recipient-search rows (cross-party)
 * @returns {{direct: Array, request: Array}}
 */
export const buildPickerSections = ({ contacts = [], search = [] } = {}) => {
  const direct = [];
  const request = [];
  for (const c of Array.isArray(contacts) ? contacts : []) {
    direct.push(normalizeContactRow(c));
  }
  for (const s of Array.isArray(search) ? search : []) {
    const row = normalizeSearchRow(s);
    (row.bucket === "request" ? request : direct).push(row);
  }
  return { direct, request };
};

// Can this normalised row actually be requested through the compose gate? The
// message-request create endpoint keys on the Agent-row PK, so only agent rows
// carrying agent_id are sendable today; club request rows are shown (5m) but
// stay disabled until an agent→club request flow exists (honest, not a bypass).
export const rowIsRequestable = (row) =>
  row?.kind === "agent" && Boolean(row?.agent_id);
